import { Drawer, Spin, Tooltip } from 'antd';
import { DeleteOutlined, DownOutlined, StarOutlined, UpOutlined } from '@ant-design/icons';
import 'moment/locale/es';
import { useFetch } from '../../../../../../hooks/useFetch/useFetch';
import { useEffect, useState } from 'react';
import { useAuth } from '../../../../../../store/AuthContext/AuthContext';
import moment from 'moment';
import TableLZM from '../../../../../components/design/table/TableLZM';
import { SENTIMENT_ICONS } from '../Label';
import { LocationCardCompacted } from '../../../../../components/model/business/FieldsGroup';
import SelectPeriod from '../../../../../components/model/selectMonths/SelectPeriod';
import { CSVDownloadServer } from '../../../../../../common/utils/CSVDownloadServer';
import { PageFilters, PageFilterField } from '../../../../common/PageFilters';
import { DrawerPanelTitle, drawerPanelStyles } from '../../../../common/Drawers/DrawerPanelTitle';
import { EmptyStateBox } from '../../../../../components/design/EmptyStateBox/EmptyStateBox';
import { PageInfoCard } from '../../../../common/PageInfoCard';
import { LocationFilterSelect, LocationFilterValue } from '../../../../../components/model/location/LocationFilterSelect';
import { ResultsPagination } from '../../../../../components/design/ResultsPagination/ResultsPagination';
import '../../../../common/PageTitle.css';
import './RemovedReviewsModal.css';

const REMOVED_REVIEWS_INFO =
	'En esta pantalla puedes ver las reseñas que han dejado de aparecer en Google.\n\nEsto pude ocurrir por varios motivos ajenos al local. Por ejemplo:\n\n- Google actualiza sus políticas de contenido\n- Se resuelve un proceso de denuncia\n- La cuenta que creó la reseña se da de baja';

const COMMENT_PREVIEW_LENGTH = 140;

const ExpandableComment = ({ text }: { text: string }) => {
	const [expanded, setExpanded] = useState(false);
	const isLong = text.length > COMMENT_PREVIEW_LENGTH;
	const displayText = !text
		? null
		: expanded || !isLong
			? text
			: `${text.slice(0, COMMENT_PREVIEW_LENGTH)}…`;

	return (
		<>
			{displayText ? (
				<p className="removed-reviews__review-text">{displayText}</p>
			) : (
				<p className="removed-reviews__review-text removed-reviews__review-text--empty">
					Sin comentario
				</p>
			)}
			{isLong && (
				<button
					type="button"
					className="removed-reviews__more"
					onClick={() => setExpanded((prev) => !prev)}
				>
					{expanded ? 'Ver menos' : 'Ver más'}
				</button>
			)}
		</>
	);
};

const getLocationId = (location: any) =>
	location?.composeId || location?.locationId || location?.name || '';

export const RemovedReviewsModal = (props: {onFinish: any}) => {

	const [reviewsData, setReviewsData] = useState<any[]>();
	const [selectedType, setSelectedType] = useState('LAST_30DAYS');
	const [selectedLocation, setSelectedLocation] = useState<LocationFilterValue | null>(null);
	const [infoExpanded, setInfoExpanded] = useState(false);
	const [pagination, setPagination] = useState<any>({ page: 1 });
	const [filterPage, setFilterPage] = useState('');
	const { projectGroup } = useAuth();

	const locationParam = selectedLocation ? `&locationId=${encodeURIComponent(selectedLocation.id)}` : '';

	useEffect(() => {
		if (filterPage !== '') {
			setFilterPage('&page=1');
		}
	}, [projectGroup, selectedType, selectedLocation?.id]);

	const { isLoading } = useFetch(
		`${process.env.REACT_APP_API}/projects/${projectGroup.project}/${projectGroup.group}/reviews/removed?periodOpt=${selectedType}${locationParam}${filterPage}`,
		{},
		[selectedType, selectedLocation?.id, filterPage],
		(data) => {
			if (Array.isArray(data)) {
				setReviewsData(data);
				setPagination({
					page: 1,
					total: data.length,
					from: data.length ? 1 : 0,
					to: data.length,
					startDisabled: true,
					endDisabled: true,
				});
				return;
			}
			setReviewsData(data?.reviews ?? []);
			setPagination(data?.pagination ?? { page: 1 });
		}
	);

	const setLocationFilter = (next: LocationFilterValue | null) => {
		if ((next?.id || null) === (selectedLocation?.id || null)) return;
		setReviewsData(undefined);
		setSelectedLocation(next);
	};

	const filterByLocation = (location: any) => {
		const id = getLocationId(location);
		if (!id) return;
		const address = [
			location?.addressLine,
			location?.postalCode,
			location?.locality,
			location?.administrativeArea,
		].filter(Boolean).join(', ') || location?.locality;
		setLocationFilter({
			id,
			title: location?.title || location?.locality || 'Local seleccionado',
			address,
		});
	};

	const columns = getColumns(filterByLocation, selectedLocation?.id);

	return (
		<Drawer
			placement="right"
			open={true}
			onClose={props.onFinish}
			width={'85%'}
			{...drawerPanelStyles}
			title={
				<DrawerPanelTitle
					title={
						<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
							Reseñas Eliminadas
							<button
								type="button"
								className="page-info-icon"
								aria-label={infoExpanded ? 'Ocultar información' : 'Mostrar información'}
								aria-expanded={infoExpanded}
								onClick={(e) => {
									e.stopPropagation();
									setInfoExpanded((prev) => !prev);
								}}
							>
								{infoExpanded ? <UpOutlined /> : <DownOutlined />}
							</button>
						</span>
					}
					isProject={false}
				/>
			}
		>
			<div className="removed-reviews">
				{infoExpanded && (
					<div className="removed-reviews__info">
						<PageInfoCard
							title="Sobre las reseñas eliminadas"
							description={REMOVED_REVIEWS_INFO}
						/>
					</div>
				)}

				<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 0 }}>
					<PageFilters>
						<PageFilterField label='Local' active={!!selectedLocation}>
							<LocationFilterSelect
								value={selectedLocation}
								onChange={setLocationFilter}
								placeholder="Todos los locales"
							/>
						</PageFilterField>
						<PageFilterField label='Periodo' active={selectedType !== 'LAST_30DAYS'}>
							<SelectPeriod setSelectedType={(value: string) => {
								setReviewsData(undefined);
								setSelectedType(value);
							}} defaultValue={selectedType} maxMonths={3}/>
						</PageFilterField>
					</PageFilters>
				</div>

				{reviewsData !== undefined && (
					<div className="removed-reviews__pagination">
						<ResultsPagination
							pagination={pagination}
							pageItemsCount={reviewsData.length}
							align='start'
							disabled={isLoading}
							onPrev={() => { setFilterPage('&page=' + (pagination.page - 1)); }}
							onNext={() => { setFilterPage('&page=' + (pagination.page + 1)); }}
						/>
					</div>
				)}

				{isLoading || reviewsData === undefined ? (
					<div className="removed-reviews__loading">
						<Spin size='large'/>
					</div>
				) : !reviewsData?.length ? (
					<div className="removed-reviews__empty">
						<EmptyStateBox
							icon={<DeleteOutlined />}
							title="No hay reseñas eliminadas"
							description="No hay reseñas eliminadas para los filtros seleccionados."
						/>
					</div>
				) : (
					<div className="removed-reviews__table">
						<TableLZM
							size='small'
							pagination={false}
							scroll={{ y: 'calc(100vh - 320px)' }}
							dataSource={reviewsData}
							columns={columns}
							rowClassName={() => 'removed-reviews__row'}
						/>
					</div>
				)}

				{!!reviewsData?.length && (
					<div className="removed-reviews__csv">
						<CSVDownloadServer
							url={`${process.env.REACT_APP_API}/projects/${projectGroup.project}/${projectGroup.group}/csv/reviews-removed?periodOpt=${selectedType}${locationParam}`}
							fileName='Lista de Reseñas Borradas.csv'
						/>
					</div>
				)}
			</div>
		</Drawer>
	)
}

const getColumns = (onFilterLocation: (location: any) => void, selectedLocationId?: string): any => [
	{
		title: 'Borrado',
		key: 'removedDate',
		width: 150,
		render: (_: string, record: any) => (
			<div className="removed-reviews__date">
				<span className="removed-reviews__date-pill">
					{moment(record.removedDate).format('D MMM YYYY')}
				</span>
				<span className="removed-reviews__date-ago">
					{moment(record.removedDate).fromNow()}
				</span>
			</div>
		),
	},
	{
		title: 'Reseña',
		key: 'text',
		dataIndex: 'text',
		width: '55%',
		render: (_: string, record: any) => {
			const review = record.syncReview;
			const fullText = review?.comment?.originalText || review?.comment?.text || '';
			const rating = review?.starRating;

			return (
				<div className="removed-reviews__review">
					<div className="removed-reviews__review-head">
						<img
							src={review.reviewerProfilePhotoUrl}
							alt=""
							className="removed-reviews__avatar"
						/>
						<div className="removed-reviews__review-meta">
							<div className="removed-reviews__reviewer">
								<span className="removed-reviews__reviewer-name">
									{review.reviewerDisplayName}
								</span>
								<span className="removed-reviews__status">Eliminada</span>
							</div>
							<Tooltip
								title={
									<>
										<div>Creada el {moment(review.createdTime).format('ll')}</div>
										{!moment(review.createdTime).isSame(moment(review.updateTime)) && (
											<div>Actualizada el {moment(review.updateTime).format('ll')}</div>
										)}
									</>
								}
							>
								<span className="removed-reviews__review-time">
									Publicada {moment(review.createdTime).fromNow()}
								</span>
							</Tooltip>
						</div>
						{!!rating && (
							<span className="removed-reviews__rating">
								<StarOutlined />
								{(Math.round(10 * rating) / 10)?.toFixed(1)}
							</span>
						)}
					</div>
					<ExpandableComment text={fullText} />
					{!!review.commentLabels?.length && (
						<div className="removed-reviews__labels">
							{review.commentLabels.map((label: any) => (
								<span
									key={`${label.category}-${label.label}`}
									className="removed-reviews__label"
								>
									{SENTIMENT_ICONS[label.sentiment]}
									<span className="removed-reviews__label-text">{label.label}</span>
									{!!label.category && (
										<span className="removed-reviews__label-category">{label.category}</span>
									)}
								</span>
							))}
						</div>
					)}
				</div>
			);
		},
	},
	{
		title: 'Dirección',
		key: 'locality',
		dataIndex: 'locality',
		align: 'center',
		render: (_: string, record: any) => {
			const location = record.syncReview;
			const locationId = getLocationId(location);
			const isSelected = !!selectedLocationId && selectedLocationId === locationId;

			return (
				<div
					role="button"
					tabIndex={0}
					onClick={(e) => {
						e.stopPropagation();
						onFilterLocation(location);
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							onFilterLocation(location);
						}
					}}
					style={{
						cursor: 'pointer',
						display: 'inline-block',
						borderRadius: 10,
						outline: isSelected ? '2px solid #93c5fd' : '2px solid transparent',
						outlineOffset: 1,
					}}
					title="Filtrar por este local"
				>
					<LocationCardCompacted
						location={location}
						color={isSelected ? '#eff6ff' : undefined}
					/>
				</div>
			);
		},
	},
]
